import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { loadFormService } from '../../services/loadforms/loadFormService'
import { CreateLoadFormForm } from '../../types'
import { showErrorToast, showSuccessToast } from '@/utils/errorHandler'

// Query keys
export const loadFormKeys = {
  all: ['loadforms'] as const,
  lists: () => [...loadFormKeys.all, 'list'] as const,
  list: (filters: Record<string, any>) => [...loadFormKeys.lists(), { filters }] as const,
  details: () => [...loadFormKeys.all, 'detail'] as const,
  detail: (id: string) => [...loadFormKeys.details(), id] as const,
  bookers: () => [...loadFormKeys.all, 'bookers'] as const,
  preview: (bookerIds: string[]) => [...loadFormKeys.all, 'preview', bookerIds] as const,
  previewSummary: (bookerIds: string[]) => [...loadFormKeys.all, 'preview-summary', bookerIds] as const,
}

// Get all bookers
export const useBookers = () => {
  return useQuery({
    queryKey: loadFormKeys.bookers(),
    queryFn: loadFormService.getBookers,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

// Get load forms with filters
export const useLoadForms = (params?: {
  page?: number
  limit?: number
  search?: string
  bookerId?: string
  status?: string
  area?: string
  date?: string
  startDate?: string
  endDate?: string
}) => {
  return useQuery({
    queryKey: loadFormKeys.list(params || {}),
    queryFn: () => loadFormService.getLoadForms(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
  })
}

// Get single load form
export const useLoadForm = (id: string) => {
  return useQuery({
    queryKey: loadFormKeys.detail(id),
    queryFn: () => loadFormService.getLoadFormById(id),
    enabled: !!id,
  })
}

// Get load form with history
export const useLoadFormHistory = (id: string) => {
  return useQuery({
    queryKey: [...loadFormKeys.detail(id), 'history'],
    queryFn: () => loadFormService.getLoadFormHistory(id),
    enabled: !!id,
  })
}

// Get active bills preview for selected bookers
export const useLoadFormPreview = (bookerIds: string[]) => {
  return useQuery({
    queryKey: loadFormKeys.preview(bookerIds),
    queryFn: () => loadFormService.getActiveBillsForBookers(bookerIds),
    enabled: bookerIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Get preview summary for selected bookers
export const useLoadFormPreviewSummary = (bookerIds: string[]) => {
  return useQuery({
    queryKey: loadFormKeys.previewSummary(bookerIds),
    queryFn: () => loadFormService.getLoadFormPreviewSummary(bookerIds),
    enabled: bookerIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds
  })
}

// Create load form mutation
export const useCreateLoadForm = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (formData: CreateLoadFormForm) => loadFormService.createLoadForm(formData),
    onSuccess: () => {
      // Invalidate and refetch load forms list
      queryClient.invalidateQueries({ queryKey: loadFormKeys.lists() })
      showSuccessToast('Load form created successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// Update load form status mutation
export const useUpdateLoadFormStatus = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'open' | 'closed' }) =>
      loadFormService.updateLoadFormStatus(id, status),
    onSuccess: (data) => {
      // Update the specific load form in cache
      queryClient.setQueryData(loadFormKeys.detail(data.id), data)
      // Invalidate lists to reflect changes
      queryClient.invalidateQueries({ queryKey: loadFormKeys.lists() })
      showSuccessToast('Load form status updated successfully!')
    },
  })
}

// Delete load form mutation
export const useDeleteLoadForm = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => loadFormService.deleteLoadForm(id),
    onSuccess: () => {
      // Invalidate and refetch load forms list
      queryClient.invalidateQueries({ queryKey: loadFormKeys.lists() })
      showSuccessToast('Load form deleted successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// Mark load form as pending mutation
export const useMarkLoadFormAsPending = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      loadFormService.markLoadFormAsPending(id, reason),
    onSuccess: () => {
      // Invalidate and refetch load forms list
      queryClient.invalidateQueries({ queryKey: loadFormKeys.lists() })
      showSuccessToast('Load form marked as pending successfully!')
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// Print load form mutation
export const usePrintLoadForm = () => {
  return useMutation({
    mutationFn: (id: string) => loadFormService.printLoadFormHtml(id),
    onSuccess: (htmlContent: string) => {
      // Open a new window with the HTML content and print it
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        printWindow.focus()
        printWindow.print()
        printWindow.close()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// Print load form with invoices mutation
export const usePrintLoadFormWithInvoices = () => {
  return useMutation({
    mutationFn: (id: string) => loadFormService.printLoadFormWithInvoicesHtml(id),
    onSuccess: (htmlContent: string) => {
      // Open a new window with the HTML content and print it
      const printWindow = window.open('', '_blank')
      if (printWindow) {
        printWindow.document.write(htmlContent)
        printWindow.document.close()
        printWindow.focus()
        printWindow.print()
        printWindow.close()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// View load form mutation (opens HTML without printing)
export const useViewLoadForm = () => {
  return useMutation({
    mutationFn: (id: string) => loadFormService.printLoadFormHtml(id),
    onSuccess: (htmlContent: string) => {
      // Open a new window with the HTML content (without printing)
      const viewWindow = window.open('', '_blank')
      if (viewWindow) {
        viewWindow.document.write(htmlContent)
        viewWindow.document.close()
        viewWindow.focus()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// View load form with invoices mutation
export const useViewLoadFormWithInvoices = () => {
  return useMutation({
    mutationFn: (id: string) => loadFormService.printLoadFormWithInvoicesHtml(id),
    onSuccess: (htmlContent: string) => {
      // Open a new window with the HTML content (without printing)
      const viewWindow = window.open('', '_blank')
      if (viewWindow) {
        viewWindow.document.write(htmlContent)
        viewWindow.document.close()
        viewWindow.focus()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}

// View invoices only mutation (opens HTML without printing)
export const useViewInvoicesOnly = () => {
  return useMutation({
    mutationFn: (id: string) => loadFormService.printLoadFormInvoicesOnlyHtml(id),
    onSuccess: (htmlContent: string) => {
      // Open a new window with the HTML content (without printing)
      const viewWindow = window.open('', '_blank')
      if (viewWindow) {
        viewWindow.document.write(htmlContent)
        viewWindow.document.close()
        viewWindow.focus()
      }
    },
    onError: (error) => {
      showErrorToast(error)
    },
  })
}